import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, RotateCcw, Calendar, Sparkles, CheckCircle2 } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import LeadCaptureForm from "@/components/lead/LeadCaptureForm";
import { submitQuiz } from "@/lib/lead-api";

/* ------- Quiz definition ------- */
const QUESTIONS = [
  {
    id: "timeline",
    title: "When would you like to be living in Spain?",
    sub: "Honestly — pick the option closest to your gut feeling.",
    options: [
      { value: "0-6", label: "Within 6 months", score: 100 },
      { value: "6-12", label: "6 to 12 months", score: 90 },
      { value: "12-24", label: "1 to 2 years", score: 60 },
      { value: "24+", label: "2+ years from now", score: 30 },
      { value: "unsure", label: "I'm not sure yet", score: 10 },
    ],
    weight: 30,
  },
  {
    id: "age",
    title: "Which age band are you in?",
    sub: "This affects S1 eligibility and insurance costs.",
    options: [
      { value: "under-55", label: "Under 55", score: 60 },
      { value: "55-64", label: "55 – 64", score: 80 },
      { value: "65-69", label: "65 – 69", score: 95 },
      { value: "70+", label: "70+", score: 90 },
    ],
    weight: 10,
  },
  {
    id: "applicants",
    title: "Single applicant or applying as a couple?",
    options: [
      { value: "single", label: "Just me", score: 90 },
      { value: "couple", label: "My partner and me", score: 90 },
      { value: "family", label: "Couple + dependants", score: 80 },
    ],
    weight: 5,
  },
  {
    id: "finance",
    title: "How confident are you in proving the financial requirement?",
    sub: "Roughly €28,800/year for the main applicant + €7,200 per dependant.",
    options: [
      { value: "very", label: "Very confident — I've got the figures and proof", score: 100 },
      { value: "mostly", label: "Mostly — I think I have enough but I'm unsure on documents", score: 70 },
      { value: "unsure", label: "Not sure — need help figuring it out", score: 40 },
      { value: "no", label: "I don't think I'd qualify yet", score: 15 },
    ],
    weight: 25,
  },
  {
    id: "s1",
    title: "Are you of UK state pension age (or about to be)?",
    sub: "S1 eligibility removes the biggest single cost — private health insurance.",
    options: [
      { value: "yes", label: "Yes — I qualify for S1", score: 95 },
      { value: "soon", label: "Within 12 months", score: 85 },
      { value: "no", label: "No, I'll need private health insurance", score: 70 },
      { value: "unsure", label: "Not sure", score: 50 },
    ],
    weight: 10,
  },
  {
    id: "stuck",
    title: "Where are you stuck right now?",
    sub: "Pick the one that's bothering you most.",
    options: [
      { value: "starting", label: "Just starting and feeling overwhelmed", score: 40 },
      { value: "qualify", label: "Unsure if I even qualify", score: 50 },
      { value: "forms", label: "Spanish forms / financial proof", score: 70 },
      { value: "timing", label: "Timing — documents expiring, when to start", score: 75 },
      { value: "tax", label: "Tax / property sale planning", score: 65 },
      { value: "ready", label: "I'm ready, just need a clear plan", score: 95 },
    ],
    weight: 15,
  },
  {
    id: "consulate",
    title: "Which UK consulate would you apply at?",
    options: [
      { value: "london", label: "London", score: 90 },
      { value: "manchester", label: "Manchester", score: 90 },
      { value: "edinburgh", label: "Edinburgh", score: 90 },
      { value: "unknown", label: "Not sure yet", score: 50 },
    ],
    weight: 5,
  },
];

function calcScore(answers) {
  let total = 0;
  let weightSum = 0;
  for (const q of QUESTIONS) {
    const a = answers[q.id];
    if (!a) continue;
    const opt = q.options.find((o) => o.value === a);
    if (opt) {
      total += (opt.score * q.weight) / 100;
      weightSum += q.weight;
    }
  }
  return Math.round((total / weightSum) * 100);
}

function tierFor(score) {
  if (score >= 80) return {
    key: "ready",
    label: "NLV-ready",
    headline: "You're NLV-ready.",
    body: "You're in the top tier. Your timeline, finances and decision-clarity all stack up. The next step is a personal call with Carley to lock in your move date and start the plan.",
    accent: "var(--terracotta)",
  };
  if (score >= 60) return {
    key: "nearly",
    label: "Nearly there",
    headline: "You're nearly there.",
    body: "Just one or two pieces need tightening — likely the documents or financial proof. A short call with Carley will fast-track exactly which gap to close first.",
    accent: "var(--terracotta)",
  };
  if (score >= 40) return {
    key: "building",
    label: "Building the foundations",
    headline: "You're building the foundations.",
    body: "You've got real intent but a few open questions. Start with the full Masterclass and the cost calculator below — by the end you'll know exactly what to do next.",
    accent: "var(--gold)",
  };
  return {
    key: "exploring",
    label: "Still exploring",
    headline: "Still exploring — and that's fine.",
    body: "You're earlier in the journey. Watch Carley's full Masterclass for the complete picture, and grab the NLV checklist so you know what's coming when you're ready.",
    accent: "var(--gold)",
  };
}

/* ------- Component ------- */
export default function Quiz() {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1, then "form", then "result"
  const [phase, setPhase] = useState("intro"); // intro | questions | form | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const score = useMemo(() => calcScore(answers), [answers]);
  const tier = useMemo(() => tierFor(score), [score]);
  const progress = phase === "questions" ? ((step) / QUESTIONS.length) * 100 : phase === "form" ? 92 : 100;

  const pickAnswer = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep(step + 1);
      else setPhase("form");
    }, 250);
  };

  const handleSubmit = async (name, email, consent) => {
    const data = {
      name,
      email,
      answers,
      score,
      tier: tier.key,
      consent_marketing: consent,
      source: "/quiz",
    };
    await submitQuiz(data);
    setResult({ name, email });
    setPhase("result");
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setPhase("intro");
    setResult(null);
  };

  const q = QUESTIONS[step];

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <SiteNav />

      <section className="pt-28 pb-20 lg:pt-32 lg:pb-24 editorial relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]" data-testid="quiz-back">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          {/* Progress bar */}
          {phase !== "intro" && (
            <div className="mt-8 h-1 rounded-full bg-[var(--ink)]/10 overflow-hidden">
              <div
                className="h-full bg-[var(--terracotta)] transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* INTRO */}
          {phase === "intro" && (
            <div className="mt-10 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
                <Sparkles className="w-3.5 h-3.5" /> Free · 2 minutes
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-6 leading-[1.04]" data-testid="quiz-headline">
                Are you <em className="text-[var(--terracotta)]">NLV-ready?</em>
              </h1>
              <p className="mt-6 text-lg text-[var(--ink-soft)] leading-relaxed">
                7 short questions. You'll get a personalised readiness score and a clear
                next step — whether that's a free call with Carley or doing your homework first.
              </p>
              <button
                onClick={() => setPhase("questions")}
                className="mt-10 pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
                data-testid="quiz-start"
              >
                Start the quiz <ArrowRight className="w-5 h-5" />
              </button>
              <p className="mt-4 text-xs text-[var(--muted)]">No spam. Email only required at the end if you want your full report.</p>
            </div>
          )}

          {/* QUESTIONS */}
          {phase === "questions" && q && (
            <div className="mt-10 fade-up" key={step}>
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">
                Question {step + 1} of {QUESTIONS.length}
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[var(--navy)] mt-4 leading-tight">
                {q.title}
              </h2>
              {q.sub && <p className="mt-3 text-[var(--ink-soft)]">{q.sub}</p>}

              <div className="mt-8 space-y-3" data-testid={`quiz-options-${q.id}`}>
                {q.options.map((o) => {
                  const selected = answers[q.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => pickAnswer(q.id, o.value)}
                      className={`w-full text-left flex items-center justify-between gap-4 px-5 py-4 rounded-2xl border transition-all duration-300 ${
                        selected
                          ? "bg-[var(--terracotta)] text-[var(--cream)] border-[var(--terracotta)]"
                          : "bg-white border-black/10 hover:border-[var(--terracotta)]/40 hover:bg-[var(--cream-deep)]/40"
                      }`}
                      data-testid={`quiz-opt-${q.id}-${o.value}`}
                    >
                      <span className={`font-medium ${selected ? "text-[var(--cream)]" : "text-[var(--ink)]"}`}>{o.label}</span>
                      <ChevronRight className="w-4 h-4 opacity-60" />
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-6 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)] inline-flex items-center gap-1"
                  data-testid="quiz-prev"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous question
                </button>
              )}
            </div>
          )}

          {/* EMAIL GATE */}
          {phase === "form" && (
            <div className="mt-10 fade-up">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">Final step</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[var(--navy)] mt-4 leading-tight">
                Where shall we send your <em>full report?</em>
              </h2>
              <p className="mt-3 text-[var(--ink-soft)]">
                You'll see your score on the next screen. Carley will follow up if your readiness suggests it's time for a call.
              </p>
              <div className="mt-8 rounded-3xl bg-white p-7 lg:p-8 shadow-xl border border-black/5">
                <LeadCaptureForm
                  onSubmit={handleSubmit}
                  ctaLabel="See my NLV readiness score"
                  fbEvent="Lead"
                  fbEventParams={{ content_name: "NLV Eligibility Quiz", source: "/quiz" }}
                />
              </div>
            </div>
          )}

          {/* RESULT */}
          {phase === "result" && (
            <div className="mt-10 fade-up">
              <div className="rounded-[2rem] bg-[var(--navy)] text-[var(--cream)] p-10 lg:p-14 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none" style={{ background: `${tier.accent}25`, filter: "blur(80px)" }} />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.3em] font-medium" style={{ color: tier.accent }}>Your NLV readiness</div>
                  <div className="mt-6 flex items-end gap-3">
                    <div className="font-serif text-7xl lg:text-8xl leading-none" data-testid="quiz-score">{score}</div>
                    <div className="pb-2 text-2xl text-[var(--cream)]/70">/ 100</div>
                  </div>
                  <h2 className="mt-8 font-serif text-3xl lg:text-5xl leading-tight">
                    {tier.headline}
                  </h2>
                  <p className="mt-5 text-lg text-[var(--cream)]/85 leading-relaxed max-w-2xl">{tier.body}</p>

                  <div className="mt-10 flex flex-wrap gap-3">
                    <a
                      href="/nlv-approved#book"
                      className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
                      data-testid="quiz-cta-book"
                    >
                      <Calendar className="w-5 h-5" /> Book my call
                    </a>
                    <Link
                      to="/masterclass"
                      className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--cream)]/25 text-[var(--cream)] hover:bg-[var(--cream)] hover:text-[var(--navy)] font-medium"
                      data-testid="quiz-cta-masterclass"
                    >
                      Watch the Masterclass <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Link to="/checklist" className="rounded-2xl p-6 bg-white border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors flex items-start gap-4" data-testid="quiz-cross-checklist">
                  <CheckCircle2 className="w-6 h-6 text-[var(--terracotta)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-serif text-lg text-[var(--navy)]">Get the NLV Readiness Checklist</div>
                    <div className="text-sm text-[var(--ink-soft)] mt-1">One page. Everything you need to apply.</div>
                  </div>
                </Link>
                <Link to="/calculator" className="rounded-2xl p-6 bg-white border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors flex items-start gap-4" data-testid="quiz-cross-calculator">
                  <Sparkles className="w-6 h-6 text-[var(--terracotta)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-serif text-lg text-[var(--navy)]">Estimate your cost</div>
                    <div className="text-sm text-[var(--ink-soft)] mt-1">Personalised range for your situation.</div>
                  </div>
                </Link>
              </div>

              <button
                onClick={restart}
                className="mt-8 inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]"
                data-testid="quiz-restart"
              >
                <RotateCcw className="w-4 h-4" /> Retake the quiz
              </button>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
      <FloatingBookCTA />
    </main>
  );
}
